import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, Filter, Check } from 'lucide-react';
import FilterSidebar from '../components/FilterSidebar';
import $ from 'jquery';
import 'datatables.net-dt'; // Keep this for jQuery extension
import axios from 'axios'; // Ensure axios is imported for API calls
import * as CONST from '../constants';

// DataTables 및 반응형 확장에 필요한 스타일시트 임포트
import 'datatables.net-dt/css/dataTables.dataTables.css';
import 'datatables.net-responsive-dt/css/responsive.dataTables.css';

// DataTables 반응형 확장 기능 활성화
import 'datatables.net-responsive';

const SireSelectionPage = ({ selectedFarm, filteredSires, setFilteredSires, selectedSires, onSireAdd, setPlanState, setDialog, filters, filterSidebarState, setFilterSidebarState, setCurrentPage }) => {
    const tableRef = useRef(null);
    const dtApi = useRef(null);
    const currentSelectedIdsRef = useRef(new Set()); // currentSelectedIds의 최신 값을 저장할 ref
    const [filteredSiresState, setFilteredSiresState] = useState([]);
    const selectedSiresRef = useRef([]); // selectedSires의 최신 값을 저장할 ref
    const [isFilterOpen, setFilterOpen] = useState(false);
    const [searchText, setSearchText] = useState(''); // Use searchText consistently
    const filteredSiresStateRef = useRef([]); // Ref for latest filteredSiresState
    const onSireAddRef = useRef(onSireAdd);   // Ref for latest onSireAdd

    const currentSelectedIds = useMemo(() => new Set(selectedSires.map(s => s.jm_semencode)), [selectedSires]);

    // currentSelectedIds가 변경될 때마다 ref를 업데이트합니다.
    // currentSelectedIds가 변경될 때마다 ref를 업데이트합니다.
    useEffect(() => {
        currentSelectedIdsRef.current = currentSelectedIds;
        if (dtApi.current) {
            // dtApi.current.draw(false); // Removed to prevent scroll jump

            // Manually update the visual state
            // Find all elements with data-sire-id (buttons and spans) in the table
            $(tableRef.current).find('[data-sire-id]').each(function () {
                const element = $(this);
                const sireId = element.data('sire-id');
                const isSelected = currentSelectedIds.has(sireId);

                if (isSelected) {
                    // Change to span if it's a button
                    if (element.hasClass('datatable-action-button')) {
                        element.replaceWith(`<span class="datatable-completed-span" data-sire-id="${sireId}"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> 목록에 있음</span>`);
                    }
                } else {
                    // Change to button if it's a span
                    if (element.hasClass('datatable-completed-span')) {
                        element.replaceWith(`<button class="datatable-action-button" data-sire-id="${sireId}">추가</button>`);
                    }
                }
            });
        }
    }, [currentSelectedIds]);

    // Keep filteredSiresRef updated for event handlers
    useEffect(() => {
        setFilteredSiresState(filteredSires);
    }, [filteredSires]);

    // selectedSires prop이 변경될 때마다 ref를 업데이트합니다.
    useEffect(() => {
        selectedSiresRef.current = selectedSires;
    }, [selectedSires]);

    // latest values update to refs
    useEffect(() => {
        filteredSiresStateRef.current = filteredSiresState;
    }, [filteredSiresState]);

    useEffect(() => {
        onSireAddRef.current = onSireAdd;
    }, [onSireAdd]);

    const handleSearch = () => {
        const query = searchText.trim().toLowerCase(); // Use searchText
        if (!query) {
            // 검색어가 없으면 원본 데이터로 복원
            setFilteredSiresState(filteredSires);
            return;
        }

        const filtered = filteredSires.filter(sire =>
            sire.jm_semencode?.toLowerCase()?.includes(query) ||
            sire.e_short?.toLowerCase()?.includes(query) ||
            sire.h_short?.toLowerCase()?.includes(query)
        );
        setFilteredSiresState(filtered);
    };

    const handleFilterApply = async (filters, typeOptions, closeSidebar = true) => {
        console.log("필터 적용:", filters, typeOptions);
        try {
            const formData = new FormData();
            formData.append('prod_rate', filters.prodRatio);
            formData.append('body_rate', filters.confRatio);
            formData.append('gen_factor', filters.gene);
            formData.append('inbreed_coeff', filters.inbreedingCoefficient);
            formData.append('ocapa_date', filters.ocapaDate);
            formData.append('nation', filters.country);
            formData.append('varieties', filters.breed);
            // 경영방향(management) 처리
            // typeOptions의 모든 항목에 대해 'Y' 또는 'N'을 설정합니다.
            if (filters.management.includes('01'))
                formData.append('HighIndex', 'Y');
            else
                formData.append('HighIndex', 'N');

            if (filters.management.includes('02'))
                formData.append('Robotic', 'Y');
            else
                formData.append('Robotic', 'N');

            if (filters.management.includes('03'))
                formData.append('ShowType', 'Y');
            else
                formData.append('ShowType', 'N');

            if (filters.management.includes('04'))
                formData.append('MgmtIntense', 'Y');
            else
                formData.append('MgmtIntense', 'N');

            const response = await axios.post('/api/ocapa', formData);
            const sirsData = response.data?.list || []; // Assuming API response has a 'list' property
            setFilteredSires(sirsData);

            // 상위 planState의 filters를 업데이트하고, 선택된 씨수소를 초기화합니다.
            if (setPlanState) {
                setPlanState(prev => ({ ...prev, filters: filters, selectedSires: [] }));
            }
        } catch (error) {
            console.error("필터 적용 중 오류 발생:", error);
            alert("필터 적용 중 오류가 발생했습니다. 다시 시도해주세요.");
            setFilteredSires([]); // Clear results or show previous results on error
        } finally {
            if (closeSidebar) {
                setFilterOpen(false); // Close the sidebar only if closeSidebar is true
            }
        }
    };

    const columns = useMemo(() => {
        const country = filters?.country;

        // 1. 공통 컬럼 - 앞부분
        const baseColumns = [
            { title: '씨수소번호', data: 'jm_semencode' },
            { title: '명호(한)', data: 'h_short' },
            { title: '명호(영)', data: 'e_short' },
            { title: 'A2', data: 'beta_casein' },
        ];

        // 3. 공통 컬럼 - 뒷부분
        const tailColumns = [
            { title: '부X외조부', data: 'siremgsmggs' },
            { title: '선택', data: null }
        ];

        // 2. 국가별 헤더 및 구성 정의
        let dynamicPart = [];

        if (country === '03') { // 캐나다
            dynamicPart = [
                { title: 'LPI', data: 'ocapa_tpi' },
                { title: 'Pro$', data: 'netmerit' },
                { title: 'Milk', data: 'pta_milk' },
                { title: 'Protein', data: 'pta_pro' },
                { title: 'Fat', data: 'pta_fat' },
                { title: 'Conformation', data: 'ptat' },
                { title: 'Mammary System', data: 'udc' },
                { title: 'Feet & Legs', data: 'flc' },
                { title: 'Herd Life', data: 'pl' },
                { title: 'Calving Ability', data: 'dpr' },
                { title: 'Daughter Calving Ability', data: 'nansan' },
                { title: 'Milking Speed', data: 'miking_speed' },
            ];
        } else if (country === '02' || country === '04') { // 미국, 기타
            dynamicPart = [
                { title: 'TPI', data: 'ocapa_tpi' },
                { title: 'NM$', data: 'netmerit' },
                { title: 'PTAM', data: 'pta_milk' },
                { title: 'PTAP', data: 'pta_pro' },
                { title: 'PTAF', data: 'pta_fat' },
                { title: 'PTAT', data: 'ptat' },
                { title: 'UDC', data: 'udc' },
                { title: 'FLC', data: 'flc' },
                { title: 'PL', data: 'pl' },
                { title: 'DPR', data: 'dpr' },
                { title: 'SCS', data: 'scs' },
                { title: 'Milking Speed', data: 'miking_speed' },
            ];
        } else { // 한국 (01) 및 기본값
            dynamicPart = [
                { title: 'KTPI', data: 'ocapa_tpi' },
                { title: 'MFP천원', data: 'netmerit' },
                { title: 'EBVM', data: 'pta_milk' },
                { title: 'EBVP', data: 'pta_pro' },
                { title: 'EBVF', data: 'pta_fat' },
                { title: 'PTAT', data: 'ptat' },
                { title: 'UDC', data: 'udc' },
                { title: 'FLC', data: 'flc' },
                { title: 'Herd Life', data: 'pl' },
                { title: 'SCS', data: 'scs' },
            ];
        }

        return [...baseColumns, ...dynamicPart, ...tailColumns];
    }, [filters?.country]);

    useEffect(() => {
        // Initialize DataTables with initial data
        // columns의 개수만큼만 target을 설정해야 빈 컬럼이 생성되지 않습니다.
        // 마지막 '선택' 컬럼(버튼)은 별도의 render 함수를 사용하므로 공통 타겟에서 제외합니다.
        const dataColumnTargets = Array.from({ length: columns.length - 1 }, (_, i) => i);
        const options = {
            columns: columns,
            responsive: {
                details: {
                    // 'inline' 타입은 첫 번째 컬럼에 + 아이콘을 표시하고,
                    // 클릭 시 바로 아래에 child row를 펼쳐줍니다.
                    type: 'inline'
                }
            },
            data: filteredSiresState, // Initial data for the table
            paging: true,
            lengthChange: false,
            pageLength: 10,
            searching: false, // 자체 검색 UI 사용
            ordering: true,
            language: {
                emptyTable: "검색 결과가 없습니다.",
                paginate: {
                    first: "처음",
                    last: "마지막",
                    next: "다음",
                    previous: "이전"
                },
                info: "_TOTAL_건 중 _START_에서 _END_까지 표시",
                infoEmpty: "0건",
                infoFiltered: "(_MAX_건에서 필터링됨)"
            },
            columnDefs: [
                {
                    targets: dataColumnTargets,
                    defaultContent: "",
                    render: function (data) {
                        return (data === null || data === 'null') ? '' : data;
                    }
                },
                { responsivePriority: 2, targets: 0, className: 'dt-body-right' }, // 씨수소번호 우측 정렬
                { targets: 1, className: 'dt-body-left' }, // 한글명호 좌측 정렬
                {
                    targets: columns.length - 1, // 추가 버튼 (마지막 컬럼)
                    // responsivePriority를 1로 설정하여 가장 마지막까지 숨겨지지 않도록 합니다.
                    // (컨트롤 컬럼(+)은 priority 0을 가집니다)
                    responsivePriority: 1,
                    orderable: false,
                    className: 'dt-body-right',
                    render: (data, type, row) => {
                        // 항상 버튼을 렌더링하고, 상태 변경은 drawCallback에서 처리합니다.
                        return `<button class="datatable-action-button" data-sire-id="${row.jm_semencode}">추가</button>`;
                    }
                }
            ],
            drawCallback: function (settings) {

                const api = this.api();

                // 반응형 재계산
                api.responsive.recalc();

                // 버튼 상태 업데이트: drawCallback은 항상 최신 currentSelectedIds에 접근 가능합니다.
                api.rows({ page: 'current' }).nodes().to$().find('.datatable-action-button').each(function () {
                    const button = $(this); // jQuery 객체로 변환
                    const sireId = button.data('sire-id');
                    if (currentSelectedIdsRef.current.has(sireId)) { // ref를 통해 최신 값 참조
                        button.replaceWith(`<span class="datatable-completed-span" data-sire-id="${sireId}"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> 목록에 있음</span>`);
                    }
                });
            }
        };

        const tableNode = tableRef.current;
        const clickHandler = function () {
            // Using .attr() to ensure we get the string value from the DOM,
            // avoiding potential issues with .data() auto-parsing digits or stale jQuery cache.
            const sireId = $(this).attr('data-sire-id');
            const sireToAdd = filteredSiresStateRef.current.find(s => s.jm_semencode === sireId);
            if (sireToAdd) {
                // 씨수소 개수 제한
                if (selectedSiresRef.current.length >= 10) {
                    setDialog({ isOpen: true, title: "제한 초과", message: "씨수소는 최대 10개까지 추가할 수 있습니다.", onClose: () => setDialog({ isOpen: false }) });
                    return;
                }
                onSireAddRef.current(sireToAdd);
            }
        };

        // DataTables 초기화 전에 테이블 요소를 비웁니다. 
        // 이는 컬럼이 변경되었을 때 이전 <thead>가 남아 컬럼 수 mismatch 오류가 발생하는 것을 방지합니다.
        $(tableNode).empty();
        dtApi.current = $(tableNode).DataTable(options);

        // 클릭 이벤트 위임 (이벤트 핸들러는 한 번만 바인딩)
        $(tableNode).on('click', '.datatable-action-button', clickHandler);

        // 컴포넌트 언마운트 시 DataTables 인스턴스 파괴
        return () => {
            $(tableNode).off('click', '.datatable-action-button', clickHandler);
            if (dtApi.current && $.fn.DataTable.isDataTable(tableNode)) {
                dtApi.current.destroy();
                dtApi.current = null;
            }
        };
    }, [columns]); // columns가 변경되면 테이블을 다시 초기화해야 합니다.

    // 데이터가 변경될 때 테이블 업데이트
    useEffect(() => {
        if (dtApi.current) {
            dtApi.current.clear().rows.add(filteredSiresState).draw();
        }
    }, [filteredSiresState]);

    return (
        <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 className="page-title">씨수소 선택 (농장: {selectedFarm.name})</h2>

            <div className="card search-bar-container">
                <div style={{ display: 'flex', flexGrow: 1, gap: '0.5rem' }}>
                    <input
                        type="search"
                        placeholder="씨수소 번호, 명호 검색"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        className="form-input"
                    />
                    <button
                        onClick={handleSearch}
                        className="action-button"
                        style={{ padding: '0.75rem 1rem' }}
                    >
                        <Search size={20} />
                    </button>
                </div>

                <button
                    onClick={() => setFilterOpen(true)}
                    className="secondary-button"
                >
                    <Filter size={20} style={{ marginRight: '0.5rem' }} />
                    씨수소 선택
                </button>
            </div>

            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151' }}>검색 결과 ({filteredSires.length}건)</h3>
                    <button
                        onClick={() => setCurrentPage(CONST.PAGE_MATING_PLAN, true)}
                        className="action-button"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.9rem', fontSize: '0.875rem' }}
                    >
                        <Check size={16} />
                        계획교배 실행
                    </button>
                </div>
                <div className="table-container">
                    <table ref={tableRef} className="display nowrap" style={{ width: '100%' }}>
                        {/* 헤더는 DataTables options에서 그리므로 비워둡니다. */}
                    </table>
                </div>
            </div>

            <FilterSidebar
                isOpen={isFilterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                selectedFarm={selectedFarm}
                setDialog={() => { }}
                filterSidebarState={filterSidebarState}
                setFilterSidebarState={setFilterSidebarState}
            />
        </div>
    );
};

export default SireSelectionPage;
